import React, { useState, useEffect, useRef } from 'react';
import {
   Box,
   Table,
   Thead,
   Tbody,
   Tr,
   Th,
   Td,
   Flex,
   Grid,
   GridItem,
   Switch,
   Text,
   HStack,
   Button,
   Checkbox,
   useColorModeValue,
   IconButton,
   useToast,
   Portal,
   Badge,
   useDisclosure,
} from '@chakra-ui/react';
import { flexRender } from '@tanstack/react-table';
import nodata from 'assets/img/nfts/no data.svg';
import { DeleteIcon, EditIcon, CloseIcon, InfoIcon } from '@chakra-ui/icons';
import { useUser } from 'contexts';
import { GoArrowBoth } from 'react-icons/go';
import { useLocation } from 'react-router-dom';
import about from 'assets/img/nfts/about.svg';
import LogView from 'components/salesDetails/LogView';
import TransactionDetailModal from './TransactionDetailModel';
import { getReturnStatus } from 'utils/util';

const formatAmount = (value) => {
   if (value === null || value === undefined || value === '') return '-';

   // Convert to number
   let num;
   if (typeof value === 'string') {
      // Remove commas and convert
      num = parseFloat(value.replace(/,/g, ''));
   } else {
      num = Number(value);
   }

   // Check if valid number
   if (isNaN(num)) return value;

   // Split integer and decimal parts
   const formatted = num.toFixed(2);
   const [integerPart, decimalPart] = formatted.split('.');
   const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

   // Always show 2 decimal places with smaller font
   return (
      <span style={{ display: 'inline-flex', alignItems: 'baseline' }}>
         <span>₹</span>
         <span style={{ marginLeft: '2px' }}>
            {formattedInteger}
            <Text as="span" fontSize={{ base: '9px', md: '10px' }}>
               .{decimalPart}
            </Text>
         </span>
      </span>
   );
};

const shouldFormatAsAmount = (columnId) => {
   const id = columnId.toLowerCase();
   const amountKeywords = [
      'amount',
      'total',
      'price',
      'rate',
      'cost',
      'value',
      'tax',
      'discount',
      'balance',
      'pendingamt',
      'amountpending',
      'debit',
      'credit',
      'quantity',
      'weight',
   ];

   return amountKeywords.some((keyword) => id.includes(keyword));
};

const ActionBar = {
   Root: ({ open, children }) => (open ? children : null),
   Positioner: ({ children }) => (
      <Flex position="fixed" bottom="0" left="0" right="0" justifyContent="center" pb="4" zIndex={100}>
         {children}
      </Flex>
   ),
   Content: ({ children }) => (
      <Flex
         bg={useColorModeValue('white', 'gray.800')}
         boxShadow="lg"
         borderRadius="md"
         px="4"
         py="2"
         alignItems="center"
         gap="3"
         maxW="90%"
         w="auto">
         {children}
      </Flex>
   ),
   SelectionTrigger: ({ children }) => <Text fontWeight="medium">{children}</Text>,
   Separator: () => <Box h="24px" w="1px" bg={useColorModeValue('gray.200', 'gray.600')} mx="2" />,
};

const DynamicTable = ({
   table,
   tableData,
   singleEntityRights,
   handleViewSingleData,
   handleStatusUpdate,
   handleImageClick,
   toggleDropdown,
   filter,
   emptyMessage = 'No data found',
   fontSize = { header: { sm: '10px', lg: '12px' }, cell: { sm: '14px' } },
   onEditClick,
   onDeleteClick,
   onMultiDeleteClick,
   onBillToBillClick,
   canDelete = true,
   columnVisibility,
   pagename = true,
   type,
   setTempData,
   totalCount,
   isLoading = false,
   entityType,
   onSyncAccept,
}) => {
   const textColor = useColorModeValue('#222222', 'white');
   const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
   const backgroundColor = useColorModeValue('white', '#101F4A');
   const { user } = useUser();
   const Admin = user?.isAdmin;
   const { isOpen, onOpen, onClose } = useDisclosure();
   const [logId, setLogId] = useState(null);

   const logIconShow = type !== 'issue' && type !== 'receive';

   // const openModal = (rowData) => {
   //    setAccountId(rowData.accountId);
   //    setIsModalOpen(true);
   // };

   const location = useLocation();

   const allowedPaths = [
      '/admin/purchase/invoice',
      '/admin/purchase/return',
      '/admin/sales/invoice',
      '/admin/sales/return',
      `/admin/sales/invoice/daybook/`,
   ];
   const [selectedRows, setSelectedRows] = useState({});
   const [selectAll, setSelectAll] = useState(false);
   const checkboxRefs = useRef({});

   // Initialize refs when tableData changes
   useEffect(() => {
      if (tableData) {
         checkboxRefs.current = {};
         tableData.forEach((row) => {
            checkboxRefs.current[row.id] = null;
         });
      }
   }, [tableData]);

   // Handle key down events
   const handleKeyDown = (e, rowId, index) => {
      if (e.key === ' ' || e.key === 'Spacebar') {
         e.preventDefault();
         handleCheckboxChange(e, rowId);
      } else if (e.key === 'ArrowDown') {
         e.preventDefault();
         const nextIndex = index + 1;
         if (tableData && nextIndex < tableData.length) {
            const nextRowId = tableData[nextIndex].id;
            const nextCheckbox = checkboxRefs.current[nextRowId];
            if (nextCheckbox) {
               nextCheckbox.focus();
            }
         }
      } else if (e.key === 'ArrowUp') {
         e.preventDefault();
         const prevIndex = index - 1;
         if (prevIndex >= 0) {
            const prevRowId = tableData[prevIndex].id;
            const prevCheckbox = checkboxRefs.current[prevRowId];
            if (prevCheckbox) {
               prevCheckbox.focus();
            }
         }
      }
   };

   const isActionsVisible =
      type === 'issue' ||
      type === 'receive' ||
      (onEditClick && (Admin || singleEntityRights?.edit)) ||
      (onDeleteClick && (Admin || (canDelete && singleEntityRights?.deleteRole))) ||
      entityType === 'unusedPurchase';

   useEffect(() => {
      setSelectedRows({});
      setSelectAll(false);
   }, [tableData]);

   if (!table) {
      console.error('Table object is required for DynamicTable component');
      return null;
   }

   const columns = table.getAllColumns().filter((column) => {
      return columnVisibility[column.id];
   });

   const getSelectedRowIds = () => {
      return Object.keys(selectedRows).filter((id) => selectedRows[id]);
   };

   const handleCheckboxChange = (e, rowId) => {
      e.stopPropagation();
      setSelectedRows((prev) => ({
         ...prev,
         [rowId]: !prev[rowId],
      }));
   };

   // Handle select all checkbox
   const handleSelectAllChange = (e) => {
      e.stopPropagation();
      const newSelectAll = !selectAll;
      setSelectAll(newSelectAll);

      const newSelectedRows = {};
      if (newSelectAll && tableData) {
         tableData.forEach((row) => {
            newSelectedRows[row.id] = true;
         });
      }
      setSelectedRows(newSelectedRows);
   };

   // Handle row click with prevention for action buttons
   const handleRowClick = (rowId, e, data) => {
      if (e.target.closest('.action-buttons') || e.target.closest('.row-select-checkbox')) {
         return;
      }

      // Otherwise call the view handler
      if (handleViewSingleData) {
         // handleViewSingleData(rowId);
         handleViewSingleData(data);
      }
      if (type === 'receive') {
         setTempData(data);
      }
   };

   // Count selected rows
   const selectedCount = getSelectedRowIds().length;

   // Handle multi-delete action
   const handleMultiDelete = () => {
      const selectedIds = getSelectedRowIds();
      const selectedIdsSet = new Set(selectedIds.map(Number));

      const finalData = tableData
         .filter((item) => selectedIdsSet.has(item.id))
         .map((fd) => ({
            spId: fd.id,
            daybookId: fd.daybookId,
            companyId: Number(user?.loginhistory?.companyId),
         }));
      if (finalData.length > 0 && onMultiDeleteClick) {
         onMultiDeleteClick(finalData);
      }
   };

   // Clear all selections
   const clearSelections = () => {
      setSelectedRows({});
      setSelectAll(false);
   };

   const isIndeterminate = selectedCount > 0 && selectedCount < (tableData?.length || 0);

   const shouldHighlightPendingAmount = (columnId) => {
      const id = columnId.toLowerCase();
      return id.includes('pending') || id.includes('pendingamt') || id.includes('amountpending');
   };

   const handleLogShow = (data) => {
      setLogId(data.id);
      onOpen();
   };

   return (
      <Box
         overflow="auto"
         position="relative"
         minH={{ base: '70vh', lg: '70vh', xl: '70vh' }}
         maxH={{ base: '70vh', lg: '70vh', xl: '70vh' }}
         borderTop="1px solid #ccc">
         <Table
            variant={'simple'}
            color={'gray.500'}
            mb={'10px'}
            width="100%"
            sx={{
               '& td': {
                  padding: '12px 10px !important',
                  verticalAlign: 'middle !important',
                  height: '40px !important',
                  maxHeight: '40px !important',
                  whiteSpace: 'nowrap !important',
                  overflow: 'hidden !important',
                  textOverflow: 'ellipsis !important',
               },
               '& th': {
                  padding: '5px 8px !important',
                  verticalAlign: 'middle !important',
                  height: '45px !important',
                  whiteSpace: 'nowrap !important',
                  overflow: 'hidden !important',
                  textOverflow: 'ellipsis !important',
               },
            }}>
            <Thead position="sticky" top={0} zIndex={1} bg={backgroundColor}>
               {table.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                     {/* Selection checkbox column */}
                     {type !== 'issuereturn' && type !== 'receivereturn' && entityType !== 'unusedPurchase' ? (
                        <Th
                           pe="10px"
                           borderColor={borderColor}
                           width="40px"
                           className="action-buttons"
                           textAlign="center">
                           <Checkbox
                              isChecked={selectAll && tableData && tableData.length > 0}
                              isIndeterminate={isIndeterminate}
                              onChange={handleSelectAllChange}
                              colorScheme="teal"
                              className="row-select-checkbox"
                           />
                        </Th>
                     ) : null}

                     {/* Actions column */}
                     {type !== 'issuereturn' && type !== 'receivereturn' && isActionsVisible && pagename === true && (
                        <Th
                           key="actions"
                           pe="10px"
                           color={textColor}
                           borderColor={borderColor}
                           display={{ base: 'none', lg: 'table-cell' }}
                           textAlign="center"
                           width="120px">
                           <Flex align="center" fontSize={fontSize.header}>
                              Actions
                           </Flex>
                        </Th>
                     )}

                     {/* Dynamic columns */}
                     {headerGroup.headers.map((header) => {
                        const isStatusColumn = header.id === 'status';
                        const isAmountColumn = shouldFormatAsAmount(header.id);
                        return (
                           <Th
                              key={header.id}
                              colSpan={header.colSpan}
                              pe="10px"
                              borderColor={borderColor}
                              color={textColor}
                              cursor="pointer"
                              display={isStatusColumn ? { base: 'none', lg: 'table-cell' } : undefined}
                              textAlign={isAmountColumn ? 'right' : 'left'}
                              justifyItems="center"
                              minWidth={isAmountColumn ? '120px' : 'auto'}>
                              <Flex
                                 align="center"
                                 fontSize={fontSize.header}
                                 color={textColor}
                                 id={header.column.columnDef.header}
                                 justifyContent={isAmountColumn ? 'flex-end' : 'flex-start'}>
                                 {/* Only show filter if not id or status column */}
                                 {header.id !== 'id' && header.id !== 'status' && filter && (
                                    <img
                                       src={filter}
                                       alt="Filter"
                                       width="12px"
                                       height="12px"
                                       style={{ marginRight: '10px', cursor: 'pointer' }}
                                       id={header.column.columnDef.header}
                                       onClick={(e) => {
                                          e.stopPropagation();
                                          handleImageClick && handleImageClick(header.column.columnDef.header);
                                          toggleDropdown && toggleDropdown();
                                       }}
                                    />
                                 )}
                                 <Text
                                    color={textColor}
                                    style={{ marginRight: '10px' }}
                                    justifyItems="center"
                                    textAlign={isAmountColumn ? 'right' : 'left'}
                                    sx={{
                                       whiteSpace: 'nowrap',
                                       overflow: 'hidden',
                                       textOverflow: 'ellipsis',
                                    }}>
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                 </Text>
                                 {/* sorting arrows */}
                                 {{
                                    asc: '↑',
                                    desc: '↓',
                                 }[header.column.getIsSorted()] ?? null}
                              </Flex>
                           </Th>
                        );
                     })}
                  </Tr>
               ))}
            </Thead>

            <Tbody>
               {!tableData || tableData.length === 0 ? (
                  <Tr justifyContent="center" alignItems="center" width="100%" height="100%">
                     <Td
                        colSpan={columns.length + (isActionsVisible ? 1 : 0) + 1}
                        justifyContent="center"
                        alignItems="center"
                        width="100%">
                        <Grid justifyContent="center" alignItems="center" gap={9}>
                           <img src={nodata} alt="No Data Found" width="200px" height="200px" />
                           <GridItem justifySelf="center">
                              <Text color={textColor}>{emptyMessage}</Text>
                           </GridItem>
                        </Grid>
                     </Td>
                  </Tr>
               ) : (
                  tableData.map((rowData, index) => {
                     return (
                        <Tr
                           key={rowData.id || index}
                           className="editable-row"
                           _hover={{ bg: 'rgba(0, 63, 140, 0.08)', cursor: 'pointer' }}
                           height="40px"
                           borderBottom="2px solid"
                           borderColor={borderColor || 'gray.200'}
                           data-selected={selectedRows[rowData.id] ? '' : undefined}
                           sx={{
                              borderBottomWidth: '2px',
                              backgroundColor: selectedRows[rowData.id] ? 'rgba(0, 63, 140, 0.05)' : 'inherit',
                           }}
                           onClick={(e) => handleRowClick(rowData.id, e, rowData)}
                           tabIndex={-1}>
                           {/* Row selection checkbox */}
                           {type !== 'issuereturn' && type !== 'receivereturn' && entityType !== 'unusedPurchase' ? (
                              <Td
                                 verticalAlign="middle"
                                 className="action-buttons"
                                 width="40px"
                                 bgColor={`${getReturnStatus(rowData, type) === 'IS RETURN' ? 'red.200' : ''}`}
                                 textAlign="center">
                                 <Checkbox
                                    ref={(el) => {
                                       checkboxRefs.current[rowData.id] = el;
                                    }}
                                    isChecked={!!selectedRows[rowData.id]}
                                    onChange={(e) => handleCheckboxChange(e, rowData.id)}
                                    colorScheme="teal"
                                    className="row-select-checkbox"
                                    tabIndex={0}
                                    onKeyDown={(e) => handleKeyDown(e, rowData.id, index)}
                                 />
                              </Td>
                           ) : null}

                           {type !== 'issuereturn' &&
                              type !== 'receivereturn' &&
                              isActionsVisible &&
                              pagename === true && (
                                 <Td
                                    verticalAlign="middle"
                                    display={{ base: 'none', lg: 'table-cell' }}
                                    fontWeight="bold"
                                    color={textColor}
                                    bgColor={`${getReturnStatus(rowData, type) === 'IS RETURN' ? 'red.200' : ''}`}
                                    className="action-buttons"
                                    textAlign="center"
                                    width="150px">
                                    {entityType === 'unusedPurchase' ? (
                                       <>
                                          <Flex justifyContent="center" alignItems="center" gap="2">
                                             <button
                                                className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                                                onClick={(e) => {
                                                   e.stopPropagation();
                                                   onSyncAccept && onSyncAccept(rowData.id);
                                                }}
                                                tabIndex={-1}>
                                                Sync Accept
                                             </button>
                                             {type !== 'issuereturn' &&
                                                type !== 'receivereturn' &&
                                                onDeleteClick &&
                                                (Admin || (canDelete && singleEntityRights?.deleteRole)) &&
                                                pagename === true &&
                                                !user.isExpired && (
                                                   <IconButton
                                                      aria-label="Delete"
                                                      icon={<DeleteIcon />}
                                                      variant="outline"
                                                      colorScheme="red"
                                                      size="sm"
                                                      className="action-buttons"
                                                      onClick={(e) => {
                                                         e.stopPropagation();
                                                         onDeleteClick(rowData.id ? rowData.id : rowData);
                                                      }}
                                                      tabIndex={-1}
                                                   />
                                                )}
                                          </Flex>
                                       </>
                                    ) : (
                                       <HStack spacing={2} className="action-buttons" justifyContent="center">
                                          {allowedPaths.some((path) => location.pathname.startsWith(path)) &&
                                             !user.isExpired &&
                                             (rowData.pendingstatus !== 'CLEAR' ? (
                                                <IconButton
                                                   aria-label="View Details"
                                                   icon={<GoArrowBoth />}
                                                   variant="ghost"
                                                   colorScheme="brand"
                                                   size="sm"
                                                   className="action-buttons"
                                                   onClick={(e) => {
                                                      e.stopPropagation();
                                                      onBillToBillClick(rowData);
                                                   }}
                                                   tabIndex={-1}
                                                />
                                             ) : (
                                                <Box display="inline-block" w="32px" h="32px" visibility="hidden" />
                                             ))}

                                          {type !== 'issuereturn' && type !== 'receivereturn' && logIconShow && (
                                             <IconButton
                                                aria-label="Log"
                                                icon={<InfoIcon />}
                                                variant="ghost"
                                                colorScheme="brand"
                                                size="sm"
                                                className="action-buttons"
                                                onClick={(e) => {
                                                   e.stopPropagation();
                                                   handleLogShow(rowData);
                                                }}
                                                tabIndex={-1}
                                             />
                                          )}

                                          {type !== 'issuereturn' &&
                                             type !== 'receivereturn' &&
                                             (type === 'issue' ||
                                                type === 'receive' ||
                                                (onEditClick &&
                                                   (Admin || singleEntityRights?.edit) &&
                                                   pagename === true)) &&
                                             !user.isExpired && (
                                                <IconButton
                                                   aria-label="Edit"
                                                   icon={<EditIcon />}
                                                   variant="ghost"
                                                   colorScheme="brand"
                                                   size="sm"
                                                   className="action-buttons"
                                                   onClick={(e) => {
                                                      e.stopPropagation();
                                                      onEditClick(rowData);
                                                   }}
                                                   tabIndex={-1}
                                                />
                                             )}

                                          {type !== 'issuereturn' &&
                                             type !== 'receivereturn' &&
                                             onDeleteClick &&
                                             (Admin || (canDelete && singleEntityRights?.deleteRole)) &&
                                             pagename === true &&
                                             !user.isExpired && (
                                                <IconButton
                                                   aria-label="Delete"
                                                   icon={<DeleteIcon />}
                                                   variant="ghost"
                                                   colorScheme="red"
                                                   size="sm"
                                                   className="action-buttons"
                                                   onClick={(e) => {
                                                      e.stopPropagation();
                                                      onDeleteClick(rowData.id ? rowData.id : rowData);
                                                   }}
                                                   tabIndex={-1}
                                                />
                                             )}
                                       </HStack>
                                    )}
                                 </Td>
                              )}

                           {/* Dynamic columns */}
                           {columns.map((column) => {
                              const isStatusColumn = column.id === 'status';
                              const isitemname = column.id === 'itemname';
                              const isserialno = column.id === 'serialno' || column.id === 'serialNo';
                              const cellValue = rowData[column.id];
                              const isAmountColumn = shouldFormatAsAmount(column.id);
                              const isStockStatus = column.id === 'stockStatus';

                              if (isStockStatus) {
                                 if (type === 'issue') {
                                    const stockStatus =
                                       rowData?.lineitems?.length === 1
                                          ? rowData?.lineitems[0]?.issueLineDetail[0]?.stockStatus
                                          : null;
                                    let badgeColor = 'blue.400';
                                    let borderColor = 'blue.400';

                                    if (stockStatus === 'STORE STOCK') {
                                       badgeColor = 'green.400';
                                       borderColor = 'green.400';
                                    } else if (stockStatus === 'PARTY STOCK') {
                                       badgeColor = 'orange.400';
                                       borderColor = 'orange.400';
                                    } else {
                                       badgeColor = 'blue.400';
                                       borderColor = 'blue.400';
                                    }

                                    return (
                                       <Td
                                          key={`${rowData.id}-${column.id}`}
                                          fontSize={fontSize.cell}
                                          minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                          height="40px"
                                          bgColor={`${getReturnStatus(rowData, type) === 'IS RETURN' ? 'red.200' : ''}`}
                                          fontWeight={500}
                                          verticalAlign="middle"
                                          textAlign="center">
                                          <Badge
                                             border="1px solid"
                                             bg="transparent"
                                             borderColor={borderColor}
                                             color={badgeColor}
                                             borderRadius="md"
                                             w="100%"
                                             textAlign="center"
                                             textTransform="capitalize"
                                             sx={{
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                             }}>
                                             {stockStatus ? stockStatus : '-'}
                                          </Badge>
                                       </Td>
                                    );
                                 }
                              }

                              if (isStatusColumn) {
                                 if (type === 'receive' || type === 'purchasein' || type === 'issue') {
                                    const lineDetailKey =
                                       type === 'receive'
                                          ? 'receiveLineDetail'
                                          : type === 'purchasein' || entityType === 'unusedPurchase'
                                             ? 'purchaseLineDetail'
                                             : 'issueLineDetail';
                                    const statusKey =
                                       type === 'receive'
                                          ? 'status'
                                          : type === 'purchasein'
                                             ? 'purchaseStatus'
                                             : 'issueStatus';
                                    const status =
                                       rowData?.lineitems?.length === 1
                                          ? rowData?.lineitems[0]?.[lineDetailKey]?.[0]?.[statusKey]
                                          : null;

                                    let badgeColor = 'blue.400';
                                    let borderColor = 'blue.400';

                                    if (status === 'CLEAR' || status === 'SOLD') {
                                       badgeColor = 'green.400';
                                       borderColor = 'green.400';
                                    } else if (status === 'FULL PENDING' || status === 'RECEIVED_FROM_VENDOR') {
                                       badgeColor = 'orange.400';
                                       borderColor = 'orange.400';
                                    } else if (status === 'RECEIVED_ONLY') {
                                       badgeColor = 'red';
                                       borderColor = 'red';
                                    } else {
                                       badgeColor = 'blue.400';
                                       borderColor = 'blue.400';
                                    }

                                    return (
                                       <Td
                                          key={`${rowData.id}-${column.id}`}
                                          fontSize={fontSize.cell}
                                          minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                          height="40px"
                                          fontWeight={500}
                                          verticalAlign="middle"
                                          bgColor={`${getReturnStatus(rowData, type) === 'IS RETURN' ? 'red.200' : ''}`}
                                          textAlign="center">
                                          <Badge
                                             border="1px solid"
                                             bg="transparent"
                                             borderColor={borderColor}
                                             color={badgeColor}
                                             borderRadius="md"
                                             w="100%"
                                             textAlign="center"
                                             textTransform="capitalize"
                                             sx={{
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                             }}>
                                             {status ? status : '-'}
                                          </Badge>
                                       </Td>
                                    );
                                 } else {
                                    return (
                                       <Td
                                          key={`${rowData.id}-${column.id}`}
                                          fontSize={fontSize.cell}
                                          minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                          color={textColor}
                                          height="40px"
                                          verticalAlign="middle"
                                          display={{ base: 'none', lg: 'table-cell' }}
                                          className="action-buttons"
                                          textAlign="center">
                                          <Switch
                                             isChecked={rowData.status === 1 || rowData.status === true}
                                             onChange={(e) => {
                                                e.stopPropagation();
                                                handleStatusUpdate && handleStatusUpdate(rowData.id, e.target.checked);
                                             }}
                                             className="action-buttons"
                                             colorScheme="teal"
                                             tabIndex={-1}
                                          />
                                       </Td>
                                    );
                                 }
                              }

                              // Apply background color for status columns
                              if (column.id.toLowerCase().includes('status')) {
                                 const status = cellValue?.toUpperCase();

                                 let badgeColor = 'blue.400';
                                 let borderColor = 'blue.400';

                                 if (status === 'CLEAR') {
                                    badgeColor = 'green.400';
                                    borderColor = 'green.400';
                                 } else if (status === 'FULL PENDING') {
                                    badgeColor = 'orange.400';
                                    borderColor = 'orange.400';
                                 } else if (status === 'PART PENDING') {
                                    badgeColor = 'blue.400';
                                    borderColor = 'blue.400';
                                 }

                                 return (
                                    <Td
                                       key={`${rowData.id}-${column.id}`}
                                       fontSize={fontSize.cell}
                                       minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                       height="40px"
                                       fontWeight={500}
                                       verticalAlign="middle"
                                       textAlign="center">
                                       <Badge
                                          border="1px solid"
                                          bg="transparent"
                                          borderColor={borderColor}
                                          color={badgeColor}
                                          borderRadius="md"
                                          w="100%"
                                          textAlign="center"
                                          textTransform="capitalize"
                                          sx={{
                                             whiteSpace: 'nowrap',
                                             overflow: 'hidden',
                                             textOverflow: 'ellipsis',
                                          }}>
                                          {cellValue}
                                       </Badge>
                                    </Td>
                                 );
                              }

                              if (isitemname) {
                                 return (
                                    <Td
                                       key={`${rowData.id}-${column.id}`}
                                       fontSize={fontSize.cell}
                                       color={textColor}
                                       bgColor={`${getReturnStatus(rowData, type) === 'IS RETURN' ? 'red.200' : ''}`}
                                       sx={{
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                       }}
                                       textAlign="center">
                                       {rowData?.lineitems?.length === 1 ? rowData?.lineitems[0]?.itemName : '-'}
                                    </Td>
                                 );
                              }

                              if (isserialno) {
                                 const serialFieldMap = {
                                    receive: 'receiveLineDetail',
                                    issue: 'issueLineDetail',
                                    purchasein: 'purchaseLineDetail',
                                    purchasert: 'purchaseReturnLineDetail',
                                    salesin: 'salesLineDetail',
                                    salesrt: 'salesReturnLineDetail',
                                    salesChallan: 'lineDetails',
                                    purchaseChallan: 'lineDetails',
                                 };

                                 const detailKey = serialFieldMap[type];

                                 const serialNo =
                                    rowData?.lineitems?.length === 1 &&
                                       rowData?.lineitems?.[0]?.[detailKey]?.length === 1
                                       ? rowData?.lineitems?.[0]?.[detailKey]?.[0]?.serialNo
                                       : '-';

                                 return (
                                    <Td
                                       key={`${rowData.id}-${column.id}`}
                                       fontSize={fontSize.cell}
                                       color={textColor}
                                       bgColor={`${getReturnStatus(rowData, type) === 'IS RETURN' ? 'red.200' : ''}`}
                                       sx={{
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                       }}>
                                       {serialNo}
                                    </Td>
                                 );
                              }

                              const isPendingHighlight = shouldHighlightPendingAmount(column.id);
                              return (
                                 <Td
                                    key={`${rowData.id}-${column.id}`}
                                    fontSize={'14px'}
                                    minW={isAmountColumn ? '120px' : { sm: '150px', md: '200px', lg: 'auto' }}
                                    color={textColor}
                                    bgColor={`${getReturnStatus(rowData, type) === 'IS RETURN' ? 'red.200' : ''}`}
                                    height="40px"
                                    fontWeight={isPendingHighlight ? 'bold' : 500}
                                    verticalAlign="middle"
                                    textAlign={isAmountColumn ? 'right' : 'left'}
                                    pr={isAmountColumn ? 4 : 0}
                                    sx={{
                                       whiteSpace: 'nowrap',
                                       overflow: 'hidden',
                                       textOverflow: 'ellipsis',
                                    }}>
                                    {isAmountColumn ? formatAmount(cellValue) : cellValue}
                                 </Td>
                              );
                           })}
                        </Tr>
                     );
                  })
               )}
            </Tbody>
         </Table>

         {/* Action Bar - Same as before */}
         <ActionBar.Root open={selectedCount > 0}>
            <Portal>
               <ActionBar.Positioner>
                  <ActionBar.Content>
                     <ActionBar.SelectionTrigger>{selectedCount} selected</ActionBar.SelectionTrigger>
                     <ActionBar.Separator />
                     <IconButton
                        aria-label="Clear selection"
                        icon={<CloseIcon />}
                        size="sm"
                        variant="ghost"
                        onClick={clearSelections}
                     />
                     {user && !user.isExpired && (
                        <Button
                           variant="outline"
                           size="sm"
                           colorScheme="red"
                           leftIcon={<DeleteIcon />}
                           onClick={handleMultiDelete}>
                           Delete
                        </Button>
                     )}
                  </ActionBar.Content>
               </ActionBar.Positioner>
            </Portal>
         </ActionBar.Root>

         <LogView isOpen={isOpen} onClose={onClose} title="Log" entityType={type} id={logId} />
      </Box>
   );
};

export default DynamicTable;
